import { ApolloError } from '@apollo/client';
import { useIonToast } from '@ionic/react';
import { cloneDeep, isNil, orderBy } from 'lodash';
import moment from 'moment';
import {
  ReactElement,
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useHasPermission } from '../../../api/authorization.provider';
import {
  GetScratchpadPlaylistActivatedDocument,
  Scratchpad,
  ScratchpadPlaylist,
  ScratchpadPlaylistItem,
  ScratchpadPlaylistItemInput,
  SubscribeToScratchpadPlaylistActivatedDocument,
  SubscribeToScratchpadPlaylistActivatedSubscriptionResult,
  SubscribeToScratchpadPlaylistActivatedSubscriptionVariables,
  UpdateScratchpadPlaylistInput,
  useCreateScratchpadPlaylistMutation,
  useGetScratchpadPlaylistActivatedQuery,
  useUpdateScratchpadPlaylistMutation,
} from '../../../api/graphql/generated';
import {
  useMachineConnected,
  useMachineOfflineToast,
} from '../../../api/hooks/machine.hooks';
import { useApolloSharedSubscription } from '../../../api/subscription/apollo-subscription.manager';
import {
  PlaylistAnalyticsOptions,
  trackAddToPlaylist,
  trackCreatedPlaylist,
  trackRemoveFromPlaylist,
  trackReorderedPlaylist,
} from '../../../config/analytics';
import i18n, { i18nKeyType } from '../../../i18n';
import { useMachineContext } from '../../../pages/machine/machine-context-provider';
import { ProgramState } from '../machine-state';

const ScratchpadPlaylistContext = createContext<
  ScratchpadPlaylistContextValue | undefined
>(undefined);

export interface ScratchpadPlaylistStatus {
  playlistIndex: number;
  scratchpadId: string;
  isActive: boolean;
  isNext: boolean;
  isLast: boolean;
  isRunning: boolean;
  isLoaded: boolean;
  label?: string;
}
export type ScratchpadPlaylistPartsStatus = Omit<
  ScratchpadPlaylistStatus,
  'playlistIndex'
>;
export interface ScratchpadPlaylistPartsStatuses {
  [scratchpadId: string]: ScratchpadPlaylistPartsStatus;
}

export interface ScratchpadPlaylistContextValue {
  playlist?: ScratchpadPlaylist;
  statuses?: ScratchpadPlaylistStatus[];
  data: {
    loading: boolean;
    error?: ApolloError;
  };
}

interface ScratchpadPlaylistContextProps {
  machineId?: string;
  children: ReactNode;
}

interface ScratchpadPlaylistHelpersProps {
  machineId?: string;
}

interface ScratchpadPlaylistHelpers {
  createNewPlaylistWithScratchpad: (
    scratchpad?: Partial<Scratchpad>,
    analyticsOptions?: PlaylistAnalyticsOptions
  ) => Promise<void>;
  addScratchpadToActivatedPlaylist: (
    id: string,
    addOptions?: { addAtIndex?: number },
    analyticsOptions?: PlaylistAnalyticsOptions
  ) => Promise<void>;
  removePlaylistItem: (playlistItemId: string) => Promise<void>;
  reorderActivatedPlaylist: (
    items: ScratchpadPlaylistItem[]
  ) => Promise<ScratchpadPlaylistItem[] | undefined>;
  updateActivatedPlaylistItem: (
    item: ScratchpadPlaylistItem
  ) => Promise<ScratchpadPlaylistItem | undefined>;
}

const useScratchpadPlaylistActivatedSubscriptionShared = (
  variables: SubscribeToScratchpadPlaylistActivatedSubscriptionVariables
): SubscribeToScratchpadPlaylistActivatedSubscriptionResult => {
  const {
    input: { machineId },
  } = variables;
  const results =
    useApolloSharedSubscription<SubscribeToScratchpadPlaylistActivatedSubscriptionResult>(
      {
        query: SubscribeToScratchpadPlaylistActivatedDocument,
        variables,
        disabled: machineId === undefined,
        subscriptionName: `activated-playlist-${machineId}`,
      }
    );

  return results;
};

const ScratchpadPlaylistContextProvider = (
  props: ScratchpadPlaylistContextProps
): ReactElement | null => {
  const { children, machineId } = props;

  const { machine } = useMachineContext();
  const programState = machine?.state?.transient?.status?.programState;

  const { data: playlistQueryData } = useGetScratchpadPlaylistActivatedQuery({
    variables: {
      input: {
        machineId: machineId as string,
      },
    },
    fetchPolicy: 'cache-first',
    skip: machineId === undefined,
  });

  const subscriptionResult = useScratchpadPlaylistActivatedSubscriptionShared({
    input: {
      machineId: machineId as string,
    },
  });

  const playlist = (subscriptionResult.data
    ?.subscribeToScratchpadPlaylistActivated ??
    playlistQueryData?.getScratchpadPlaylistActivated) as ScratchpadPlaylist;

  const loading = subscriptionResult?.loading;
  const error = subscriptionResult?.error;

  const lastProcessedPlaylist = useRef<ScratchpadPlaylist | undefined>();

  const value = useMemo<ScratchpadPlaylistContextValue>(() => {
    const statuses: ScratchpadPlaylistStatus[] = [];
    const items = orderBy(playlist?.items ?? [], 'order');

    if (playlist) {
      let runningStatus: ScratchpadPlaylistStatus | undefined;

      items.forEach((item, index) => {
        const isActive = playlist.activeIndex === index;
        const isLast = items.length - 1 === index;
        const isNext =
          playlist.nextIndex === index ||
          (index === 0 && playlist.nextIndex === -1);

        let isRunning = false;

        let key: i18nKeyType | undefined;

        if (isActive) {
          if (
            [
              ProgramState.PROGRAM_PLAYING,
              ProgramState.PROGRAM_PAUSED,
            ].includes(programState)
          ) {
            isRunning = true;
            key = 'common:scratchpad_now_playing';
          } else if (isNext) {
            key = 'common:scratchpad_up_next';
          }
        } else if (isNext) {
          key = 'common:scratchpad_up_next';
        }

        const status: ScratchpadPlaylistStatus = {
          playlistIndex: index,
          scratchpadId: item.scratchpadId,
          isActive,
          isLast,
          isNext,
          isRunning,
          // we will loop through all items after generating individual statuses to set
          // the correct on to loaded
          isLoaded: false,
          label: key ? i18n.t(key) : undefined,
        };

        // optimization of keeping the current running status so we can properly identify
        // the "loaded" playlist item
        if (isRunning) {
          runningStatus = status;
        }

        statuses.push(status);
      });

      // compute which status should be considered loaded. we need to use the previous playlist value
      // to prevent a scenario where we receive a playlist update through our subscription before
      // we get the machine state which means we might mark the incorrect item as loaded
      for (const status of statuses) {
        let isLoaded = false;

        if (status.isRunning && status.isActive) {
          isLoaded = true;
        }
        // this condition catches an edge case where this status should be considered loaded but the machine
        // state subscription still hasn't propagated through the program state change so we could see a small
        // blip on the UI marking an incorrect item as loaded even though it should be this one. because we
        // are inferring that it the correct loaded one we could ahead and make sure the label shows as playing
        // so there isn't a delay waiting on the program state to come through
        else if (
          status.isActive === true &&
          !isNil(lastProcessedPlaylist.current) &&
          playlist.activeIndex !== lastProcessedPlaylist.current.activeIndex
        ) {
          status.label = i18n.t('common:scratchpad_now_playing');
          isLoaded = true;
        } else if (runningStatus === undefined && status.isNext) {
          isLoaded = true;
        }

        status.isLoaded = isLoaded;

        if (isLoaded) {
          break;
        }
      }
    }

    lastProcessedPlaylist.current = playlist;

    return {
      playlist: {
        ...playlist,
        items,
      },
      statuses,
      data: {
        loading,
        error,
      },
    };
  }, [playlist, loading, error, programState]);

  return (
    <ScratchpadPlaylistContext.Provider value={value ?? {}}>
      {children}
    </ScratchpadPlaylistContext.Provider>
  );
};

const useScratchpadPlaylistContext = (): ScratchpadPlaylistContextValue => {
  const val = useContext(ScratchpadPlaylistContext);
  return (val ?? {}) as ScratchpadPlaylistContextValue;
};

const useScratchpadPlaylistPartsContext =
  (): ScratchpadPlaylistPartsStatuses => {
    const { statuses } = useScratchpadPlaylistContext();

    const partsStatus: ScratchpadPlaylistPartsStatuses = {};

    if (statuses) {
      for (const status of statuses) {
        const existingStatus = partsStatus[status.scratchpadId] ?? {};
        const existingLabel: string | undefined = existingStatus?.label;
        let label: string | undefined = status.label;
        if (
          [existingLabel, status.label].includes(
            i18n.t('common:scratchpad_now_playing')
          )
        ) {
          label = i18n.t('common:scratchpad_now_playing');
        } else if (
          [existingLabel, status.label].includes(
            i18n.t('common:scratchpad_up_next')
          )
        ) {
          label = i18n.t('common:scratchpad_up_next');
        }

        partsStatus[status.scratchpadId] = {
          isActive: status.isActive || existingStatus.isActive,
          isLast: status.isLast || existingStatus.isLast,
          isLoaded: status.isLoaded || existingStatus.isLoaded,
          isNext: status.isNext || existingStatus.isNext,
          isRunning: status.isRunning || existingStatus.isRunning,
          scratchpadId: status.scratchpadId,
          label,
        };
      }
    }

    return partsStatus;
  };

const useIsScratchpadInPlaylist = (scratchpadId: string): boolean => {
  const { playlist } = useScratchpadPlaylistContext();

  const playlistItems: ScratchpadPlaylistItem[] = playlist?.items ?? [];

  const isScratchpadInPlaylist = playlistItems
    .map((item) => item.scratchpadId)
    .includes(scratchpadId);

  return isScratchpadInPlaylist;
};

const useScratchpadPlaylistHelpers = ({
  machineId,
}: ScratchpadPlaylistHelpersProps): ScratchpadPlaylistHelpers => {
  const { i18n } = useTranslation();
  const { data: activatedPlaylistData } =
    useGetScratchpadPlaylistActivatedQuery({
      variables: {
        input: {
          machineId: machineId as string,
        },
      },
      fetchPolicy: 'cache-first',
      skip: machineId === undefined,
    });

  const [createPlaylist] = useCreateScratchpadPlaylistMutation();
  const { machine } = useMachineContext();
  const isConnected = useMachineConnected({ machine });

  const { showMachineOfflineToast } = useMachineOfflineToast();

  const [presentToast, _dismissToast] = useIonToast();
  const [updateScratchpadPlaylist] = useUpdateScratchpadPlaylistMutation();

  const displayToastIfOffline = useCallback(() => {
    if (isConnected !== true) {
      showMachineOfflineToast();
    }
  }, [isConnected, showMachineOfflineToast]);

  const createNewPlaylistWithScratchpad = async (
    scratchpad?: Partial<Scratchpad>,
    analyticsOptions?: PlaylistAnalyticsOptions
  ) => {
    if (scratchpad?.id && machineId) {
      await createPlaylist({
        variables: {
          input: {
            activated: true,
            machineId,
            name: moment().format(),
            items: [{ order: 1, quantity: 1, scratchpadId: scratchpad.id }],
          },
        },
        refetchQueries: [
          {
            query: GetScratchpadPlaylistActivatedDocument,
            variables: { input: { machineId } },
          },
        ],
      });
      await presentToast({
        message: i18n.t('common:scratchpad_playlist_create_new_success'),
        color: 'success',
        position: 'top',
        duration: 3000,
        onDidDismiss: () => {
          displayToastIfOffline();
        },
      });
      trackCreatedPlaylist(analyticsOptions);
    }
  };

  const addScratchpadToActivatedPlaylist = async (
    scratchpadId: string,
    addOptions?: { addAtIndex?: number },
    analyticsOptions?: PlaylistAnalyticsOptions
  ) => {
    if (activatedPlaylistData?.getScratchpadPlaylistActivated) {
      const playlist = mapScratchpadPlaylistToGraphqlUpdateInput(
        activatedPlaylistData.getScratchpadPlaylistActivated
      );
      const items: Partial<ScratchpadPlaylistItemInput>[] =
        cloneDeep(
          playlist.items?.map((i) => {
            return mapScratchpadPlaylistItemToGraphql(
              i as ScratchpadPlaylistItem
            );
          })
        ) ?? [];
      items.splice(addOptions?.addAtIndex ?? items.length, 0, {
        scratchpadId,
        quantity: 1,
      });
      for (let i = 0; i < items.length; i++) {
        items[i].order = i;
      }
      await updateScratchpadPlaylist({
        variables: { input: { ...playlist, items } },
      });
      await presentToast({
        message: i18n.t('common:scratchpad_playlist_add_part_success'),
        color: 'success',
        position: 'top',
        duration: 3000,
        onDidDismiss: () => {
          displayToastIfOffline();
        },
      });
      trackAddToPlaylist(analyticsOptions);
    }
  };

  const removePlaylistItem = async (playlistItemId: string) => {
    if (activatedPlaylistData?.getScratchpadPlaylistActivated) {
      const playlist = mapScratchpadPlaylistToGraphqlUpdateInput(
        activatedPlaylistData.getScratchpadPlaylistActivated
      );

      const items: Partial<ScratchpadPlaylistItemInput>[] =
        cloneDeep(
          playlist.items
            ?.filter((i) => i.id !== playlistItemId)
            .map((i, index) => {
              return {
                ...mapScratchpadPlaylistItemToGraphql(
                  i as ScratchpadPlaylistItem
                ),
                order: index,
              };
            })
        ) ?? [];

      await updateScratchpadPlaylist({
        variables: { input: { ...playlist, items } },
      });
      displayToastIfOffline();
      trackRemoveFromPlaylist();
    }
  };

  const reorderActivatedPlaylist = async (
    items: ScratchpadPlaylistItem[]
  ): Promise<ScratchpadPlaylistItem[] | undefined> => {
    if (activatedPlaylistData?.getScratchpadPlaylistActivated) {
      const playlist = mapScratchpadPlaylistToGraphqlUpdateInput(
        activatedPlaylistData.getScratchpadPlaylistActivated
      );

      const orderedItems: ScratchpadPlaylistItemInput[] =
        cloneDeep(
          items.map((i, index) => {
            return {
              ...mapScratchpadPlaylistItemToGraphql(i),
              order: index + 1,
            };
          })
        ) ?? [];

      const result = await updateScratchpadPlaylist({
        variables: { input: { ...playlist, items: orderedItems } },
      });
      displayToastIfOffline();
      trackReorderedPlaylist();
      return result.data?.updateScratchpadPlaylist.items ?? undefined;
    }
  };

  const updateActivatedPlaylistItem = async (
    updatedItem: ScratchpadPlaylistItem
  ): Promise<ScratchpadPlaylistItem | undefined> => {
    if (activatedPlaylistData?.getScratchpadPlaylistActivated) {
      const playlist = mapScratchpadPlaylistToGraphqlUpdateInput(
        activatedPlaylistData.getScratchpadPlaylistActivated
      );

      const updatedItems: ScratchpadPlaylistItemInput[] =
        cloneDeep(
          playlist.items?.map((item) => {
            if (item.id === updatedItem.id) {
              return mapScratchpadPlaylistItemToGraphql(updatedItem);
            } else {
              return mapScratchpadPlaylistItemToGraphql(
                item as ScratchpadPlaylistItem
              );
            }
          })
        ) ?? [];

      const updatePaylistResponse = await updateScratchpadPlaylist({
        variables: { input: { ...playlist, items: updatedItems } },
      });

      displayToastIfOffline();

      const savedItem =
        updatePaylistResponse.data?.updateScratchpadPlaylist?.items?.find(
          (i) => i.id === updatedItem.id
        );

      return savedItem;
    }
  };

  return {
    createNewPlaylistWithScratchpad,
    addScratchpadToActivatedPlaylist,
    reorderActivatedPlaylist,
    updateActivatedPlaylistItem,
    removePlaylistItem,
  };
};

const useScratchpadEnhancedPlaylistPermissionCheck = (
  subject: string,
  field: string
) => {
  // use empty object for permission check since we are checking update ability on the field
  // only and it isn't for a "particular" playlist
  return useHasPermission('update', subject, {}, field);
};

const useScratchpadPlaylistCanRecallPlaylist = (): boolean => {
  return useScratchpadEnhancedPlaylistPermissionCheck(
    'scratchpad_playlist',
    'canRestore'
  );
};

const useScratchpadPlaylistToggleAutoplay = (): boolean => {
  return useScratchpadEnhancedPlaylistPermissionCheck(
    'scratchpad_playlist_item',
    'autoPlayToNext'
  );
};

const mapScratchpadPlaylistToGraphqlUpdateInput = (
  playlist: ScratchpadPlaylist
): UpdateScratchpadPlaylistInput => {
  return {
    id: playlist.id,
    machineId: playlist.machineId,
    activated: playlist.activated,
    canRestore: playlist.canRestore,
    items: orderBy(playlist.items ?? [], 'order'),
    name: playlist.name,
  };
};

const mapScratchpadPlaylistItemToGraphql = (
  item: ScratchpadPlaylistItem
): ScratchpadPlaylistItemInput => {
  return {
    id: item.id,
    autoPlayToNext: item.autoPlayToNext,
    order: item.order,
    quantity: item.quantity,
    scratchpadId: item.scratchpadId,
  };
};

export {
  ScratchpadPlaylistContextProvider,
  mapScratchpadPlaylistItemToGraphql,
  mapScratchpadPlaylistToGraphqlUpdateInput,
  useIsScratchpadInPlaylist,
  useScratchpadPlaylistActivatedSubscriptionShared,
  useScratchpadPlaylistCanRecallPlaylist,
  useScratchpadPlaylistContext,
  useScratchpadPlaylistHelpers,
  useScratchpadPlaylistPartsContext,
  useScratchpadPlaylistToggleAutoplay,
};
